<script setup>
import { ref, computed, watch } from 'vue'
import { useNotificationStore } from '@/stores/notifications'

defineOptions({ name: 'AppNotifications' })

const open = ref(false)
const notifications = useNotificationStore()

const count = computed(() => notifications.count)
const pillText = computed(() => (count.value > 99 ? '99+' : String(count.value)))

const notificationButtonLabel = computed(() => {
  if (count.value === 1) {
    return 'Open notifications, 1 unread notification'
  }

  if (count.value > 1) {
    return `Open notifications, ${count.value} unread notifications`
  }

  return 'Open notifications'
})

const notificationButtonTitle = computed(() => {
  if (count.value > 0) {
    return `Notifications (${pillText.value})`
  }

  return 'Notifications'
})

function toggleNotifications() {
  open.value = !open.value
}

function removeNotification(id) {
  notifications.remove(id)
}

function clearAllNotifications() {
  notifications.clear()
  open.value = false
}

function notificationTypeLabel(type) {
  if (type === 'success') return 'Success'
  if (type === 'error') return 'Error'
  if (type === 'bucket') return 'Notification'
  return 'Information'
}

watch(
  () => notifications.localNotificationVersion,
  (newVersion, oldVersion) => {
    if (newVersion > oldVersion) {
      open.value = true
    }
  }
)
</script>

<template>
  <div class="notifications-slot">
    <button
      type="button"
      class="notification-trigger"
      data-tutorial="notifications-trigger"
      :aria-expanded="open"
      aria-haspopup="dialog"
      aria-controls="notifications-panel"
      :aria-label="notificationButtonLabel"
      :title="notificationButtonTitle"
      @click="toggleNotifications"
    >
      <svg
        aria-hidden="true"
        focusable="false"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 7h18s-3 0-3-7" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>

      <span
        v-if="count > 0"
        class="badge badge--danger notification-count"
        aria-hidden="true"
      >
        {{ pillText }}
      </span>
    </button>

    <section
      id="notifications-panel"
      class="notifications-panel"
      data-tutorial="notifications-panel"
      :class="{ hidden: !open }"
      role="dialog"
      aria-label="Notifications"
      aria-live="polite"
    >
      <div
        v-if="notifications.sortedItems.length > 0"
        class="notification-list"
      >
        <article
          v-for="notification in notifications.sortedItems"
          :key="notification.id"
          class="notification-row"
        >
          <RouterLink
            v-if="notification.target"
            :to="notification.target"
            class="notification-row__content notification-row__link"
            @click="open = false"
          >
            <span
              class="notification-type-icon"
              aria-hidden="true"
            >
              <span v-if="notification.type === 'success'">✅</span>
              <span v-else-if="notification.type === 'error'">❌</span>
              <span v-else-if="notification.type === 'bucket'">🔔</span>
              <span v-else>ℹ️</span>
            </span>

            <span class="sr-only">
              {{ notificationTypeLabel(notification.type) }}:
            </span>

            <p class="notification-message">
              {{ notification.message }}
            </p>
          </RouterLink>

          <div
            v-else
            class="notification-row__content"
          >
            <span
              class="notification-type-icon"
              aria-hidden="true"
            >
              <span v-if="notification.type === 'success'">✅</span>
              <span v-else-if="notification.type === 'error'">❌</span>
              <span v-else-if="notification.type === 'bucket'">🔔</span>
              <span v-else>ℹ️</span>
            </span>

            <span class="sr-only">
              {{ notificationTypeLabel(notification.type) }}:
            </span>

            <p class="notification-message">
              {{ notification.message }}
            </p>
          </div>

          <button
            type="button"
            class="notification-remove"
            :aria-label="`Remove notification: ${notification.message}`"
            title="Remove notification"
            @click.stop="removeNotification(notification.id)"
          >
            <span aria-hidden="true">×</span>
          </button>
        </article>
      </div>

      <p
        v-else
        class="notification-empty"
      >
        No notifications
      </p>

      <footer class="notification-footer">
        <span class="notification-count-text">
          {{ notifications.count }} Notification(s)
        </span>

        <button
          type="button"
          class="notification-clear"
          aria-label="Clear all notifications"
          title="Clear all notifications"
          @click="clearAllNotifications"
        >
          <span aria-hidden="true">🗑️</span>
        </button>
      </footer>
    </section>
  </div>
</template>
